import React from 'react';
import { Alert, Button, Flex, Spin, Steps, Typography, theme } from 'antd';

import { describeGscConnectionError } from './GscConnectionErrors';
import SearchPerformanceAuthStep from './SearchPerformanceAuthStep';
import SearchPerformanceDomainStep from './SearchPerformanceDomainStep';
import SearchPerformanceDomainSyncCard from './SearchPerformanceDomainSyncCard';
import type { GscConnectionFlow } from './useGscConnectionFlow';

type Props = {
  flow: GscConnectionFlow;
  /** Leaves for the dashboard. */
  onDone: () => void;
};

function GscConnectionWizard({ flow, onDone }: Props) {
  const { token } = theme.useToken();
  const {
    isStatusLoading,
    isConnecting,
    isGoingBack,
    error,
    connection,
    isConnectedStep,
    isSyncComplete,
    currentStep,
    syncingDomainNames,
    selectedPropertyCount,
    connect,
    goBack,
    completeActivation,
    connectedAccountEmail,
  } = flow;

  const syncDescription = isConnectedStep && !isSyncComplete ? 'In progress...' : undefined;
  const errorCopy = error ? describeGscConnectionError(error.code) : null;

  // The way out is not a button: `SearchPerformanceConnect` auto-forwards an
  // in-session finisher, so the only affordance here is the link for a bare
  // arrival at an already-synced connection (State A), which we deliberately do
  // not auto-forward.
  function renderConnectedStep() {
    const showDashboardLink = isSyncComplete && selectedPropertyCount === 0;
    return (
      <>
        <SearchPerformanceDomainSyncCard domains={syncingDomainNames} ready={isSyncComplete} />
        {showDashboardLink && (
          <Flex justify="flex-end">
            <Button type="primary" size="large" onClick={onDone}>
              Go to search performance
            </Button>
          </Flex>
        )}
      </>
    );
  }

  return (
    <Flex vertical gap={token.marginLG}>
      <Flex vertical gap={token.marginXS}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Search Performance
        </Typography.Title>
        <Typography.Text type="secondary">
          See Prerender&apos;s data with Google Search Performance signals.
        </Typography.Text>
      </Flex>

      <Steps
        current={currentStep}
        items={[
          {
            title: 'Authenticate with Google',
            description: connectedAccountEmail,
          },
          {
            title: 'Choose domains to sync',
            description: selectedPropertyCount > 0
              ? `${selectedPropertyCount} ${selectedPropertyCount === 1 ? 'property' : 'properties'} selected`
              : undefined,
          },
          { title: 'Sync data from GSC', description: syncDescription },
        ]}
      />

      {isStatusLoading ? (
        <Flex justify="center" style={{ paddingTop: token.paddingLG }}>
          <Spin />
        </Flex>
      ) : isConnectedStep ? (
        renderConnectedStep()
      ) : connection ? (
        <SearchPerformanceDomainStep
          sessionId={connection.sessionId}
          sourceId={connection.sourceId}
          lostUrls={flow.lostUrls}
          isGoingBack={isGoingBack}
          onBack={goBack}
          onActivated={completeActivation}
        />
      ) : (
        <>
          {/* Above the card, not inside it: the whole step failed, not one field within
              it, and from here the banner sits directly under the stepper so the reader
              meets it before the sign-in button rather than after scrolling past the
              illustration. Not closable — dismissing it would leave the step looking
              untouched. */}
          {errorCopy && (
            <Alert
              type="error"
              showIcon
              message={errorCopy.message}
              description={
                <span style={{ display: 'block', maxWidth: 640 /* FIXME: no exact token */ }}>
                  {errorCopy.description}
                </span>
              }
            />
          )}
          <SearchPerformanceAuthStep isConnecting={isConnecting} onConnect={connect} />
        </>
      )}
    </Flex>
  );
}

export default GscConnectionWizard;
